/**
 * Browser-only helpers for Meta Pixel (`fbq`).
 * Fire-and-forget: never throws to callers; safe when script is not loaded yet.
 */

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
  }
}

function safeFbq(
  command: "track",
  eventName: string,
  params?: Record<string, unknown>,
): void {
  if (typeof window === "undefined") return;
  const fbq = window.fbq;
  if (typeof fbq !== "function") return;
  try {
    if (params !== undefined && Object.keys(params).length > 0) {
      fbq(command, eventName, params);
    } else {
      fbq(command, eventName);
    }
  } catch {
    // Intentionally ignore analytics failures
  }
}

function safeFbqTrackCustom(
  eventName: string,
  params?: Record<string, unknown>,
): void {
  if (typeof window === "undefined") return;
  const fbq = window.fbq;
  if (typeof fbq !== "function") return;
  try {
    if (params !== undefined && Object.keys(params).length > 0) {
      fbq("trackCustom", eventName, params);
    } else {
      fbq("trackCustom", eventName);
    }
  } catch {
    // Intentionally ignore analytics failures
  }
}

/** How the user reached WhatsApp from the item page (for funnel breakdown). */
export type MetaWhatsAppIntentType = "chat_to_buy" | "payment_receipt";

/**
 * Standard event: product / listing detail view.
 * @see https://developers.facebook.com/docs/meta-pixel/reference
 */
export function trackMetaViewContent(input: {
  itemId: string;
  title: string;
  valueNgn: number;
}): void {
  const { itemId, title, valueNgn } = input;
  if (!itemId) return;

  safeFbq("track", "ViewContent", {
    content_ids: [itemId],
    content_type: "product",
    content_name: title,
    value: valueNgn,
    currency: "NGN",
  });
}

/**
 * Standard event: search submitted on marketplace.
 */
export function trackMetaSearch(searchString: string): void {
  const q = searchString.trim();
  if (!q) return;

  safeFbq("track", "Search", {
    search_string: q,
  });
}

/**
 * Custom event: user is sent to WhatsApp to complete or discuss a purchase
 * (manual sale model). Use `intent_type` to separate chat-first vs bank-transfer receipt.
 */
export function trackMetaWhatsAppIntent(input: {
  itemId: string;
  title: string;
  valueNgn: number;
  intentType: MetaWhatsAppIntentType;
}): void {
  const { itemId, title, valueNgn, intentType } = input;
  if (!itemId) return;

  safeFbqTrackCustom("WhatsAppIntent", {
    content_ids: [itemId],
    content_type: "product",
    content_name: title,
    value: valueNgn,
    currency: "NGN",
    intent_type: intentType,
  });
}

/**
 * Standard event: item request (buyer demand) submitted.
 */
export function trackMetaLead(input: {
  contentName: string;
  contentCategory: string;
  valueNgn?: number;
}): void {
  const name = input.contentName.trim();
  if (!name) return;

  const payload: Record<string, unknown> = {
    content_name: name.slice(0, 200),
    content_category: input.contentCategory,
  };
  if (
    typeof input.valueNgn === "number" &&
    Number.isFinite(input.valueNgn) &&
    input.valueNgn >= 0
  ) {
    payload.value = input.valueNgn;
    payload.currency = "NGN";
  }

  safeFbq("track", "Lead", payload);
}

/**
 * Custom event: seller submitted a new listing for review (pending approval).
 */
export function trackMetaSubmitListing(input: {
  itemId: string;
  title: string;
  valueNgn: number;
}): void {
  const { itemId, title, valueNgn } = input;
  if (!itemId) return;

  safeFbqTrackCustom("SubmitListing", {
    content_ids: [itemId],
    content_type: "product",
    content_name: title.slice(0, 200),
    value: valueNgn,
    currency: "NGN",
  });
}

export type MetaCompleteRegistrationMethod =
  | "email_verification"
  | "oauth_profile";
export type MetaNewsletterSource =
  | "onboarding_tour_actions"
  | "profile_settings"
  | "sell_success";
export type MetaOnboardingTourSource =
  | "homepage_autoshow"
  | "floating_help"
  | "profile_tab";
export type MetaOnboardingTourAudience = "guest" | "auth";
export type MetaOnboardingTourLifecycle = "start" | "skip" | "complete";

/**
 * Standard event: registration / account setup completed (verified email or OAuth profile).
 */
export function trackMetaCompleteRegistration(
  method: MetaCompleteRegistrationMethod,
): void {
  safeFbq("track", "CompleteRegistration", {
    status: "completed",
    content_category: method,
  });
}

export function trackMetaNewsletterPopupClick(
  source: MetaNewsletterSource,
): void {
  safeFbqTrackCustom("NewsletterPopupClick", {
    source,
  });
}

export function trackMetaOnboardingTourOpen(input: {
  source: MetaOnboardingTourSource;
  audience: MetaOnboardingTourAudience;
}): void {
  safeFbqTrackCustom("OnboardingTourOpen", {
    source: input.source,
    audience: input.audience,
  });
}

export function trackMetaOnboardingTourLifecycle(input: {
  source: MetaOnboardingTourSource;
  audience: MetaOnboardingTourAudience;
  event: MetaOnboardingTourLifecycle;
}): void {
  safeFbqTrackCustom("OnboardingTourLifecycle", {
    source: input.source,
    audience: input.audience,
    lifecycle_event: input.event,
  });
}
